import * as React from 'react';

import classNames from 'classnames';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Checkbox} from 'shells/checkbox/checkbox';
// @ts-expect-error - imported from .js/.jsx file
import {SolidClockIcon} from 'shells/icon/svg/solid-clock-icon';
import {Stack} from 'shells/layout/stack/stack';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';

import {appHistory} from '../../../history';
import {GraphQLSelectActivityTypes} from '../../../ui/core/select/graphql-select-activity-types';

import {durationOptions, defaultDurationOption} from './consts';
import type {ContentType} from './timeline-creator';
import type {ActivityFormData, LocationVariant, NoteFormData} from './timeline-creator-form';

import styles from './timeline-creator-footer-bar.module.css';

type Props = {
    contentType: ContentType;
    entityId: string;
    entityName: string;
    plaintextValue: string;
    clearEditorInput: () => void;
    onSubmit: (
        e: React.SyntheticEvent,
        activityFormData?: ActivityFormData | null | undefined,
        noteFormData?: NoteFormData | null | undefined
    ) => Promise<any>;
    isLoading: boolean;
    isDisabled: boolean;
    getSubmitButtonRef: any;
    variant?: LocationVariant;
    onClose?: () => void;
    setHasActivityFormDataChanged?: (hasChanged: boolean) => void;
    activityTypeId: string | null | undefined;
    setActivityTypeId: (arg1?: string | null | undefined) => void;
    durationMinutes: number;
    setDurationMinutes: (arg1: number) => void;
    pinNote: boolean;
    setPinNote: (arg1: boolean) => void;
    onResetAfterSubmit?: () => void;
};

export function TimelineCreatorFooterBar(props: Props) {
    const {
        contentType,
        entityId,
        entityName,
        plaintextValue,
        clearEditorInput,
        onSubmit,
        isLoading,
        isDisabled,
        getSubmitButtonRef,
        variant,
        setHasActivityFormDataChanged,
        activityTypeId,
        setActivityTypeId,
        durationMinutes,
        setDurationMinutes,
        pinNote,
        setPinNote,
        onResetAfterSubmit,
    } = props;

    // Used for activities
    const defaultActivityType =
        typeof NutshellFrontendSettings !== 'undefined'
            ? NutshellFrontendSettings.defaultActivityType
            : null;

    const isActivity = contentType === 'ACTIVITY';
    const isNote = contentType === 'NOTE';

    const hasActivityFormDataChanged =
        activityTypeId !== defaultActivityType || durationMinutes !== defaultDurationOption.value;

    React.useEffect(() => {
        if (isActivity && setHasActivityFormDataChanged) {
            setHasActivityFormDataChanged(hasActivityFormDataChanged);
        }
    }, [isActivity, hasActivityFormDataChanged, setHasActivityFormDataChanged]);

    const localStyles = {
        // @ts-expect-error
        control: (base) => ({
            ...base,
            zIndex: 9,
            width: 160,
            height: 31,
        }),
    } as const;

    // Handle sending up data onSubmit
    const handleSubmit = (e: React.SyntheticEvent) => {
        onSubmit(
            e,
            isActivity ? {activityTypeId, durationMinutes} : undefined,
            isNote ? {pinNote} : undefined
        ).then(() => {
            if (onResetAfterSubmit) {
                onResetAfterSubmit();
            }
        });
    };

    const handleClose = () => {
        clearEditorInput();
        props.onClose?.();
    };

    // Submit when user presses command + enter
    // @ts-expect-error
    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    // For activities, when the 'More' button is clicked
    const onClickMore = isActivity
        ? () => {
              let data = {
                  id: 'new',
                  activityTypeId: activityTypeId ? activityTypeId.split('-')[0] : null,
                  activityDuration: durationMinutes,
                  note: plaintextValue,
                  participants: [],
              };

              if (entityName === 'Leads') {
                  const leadId = entityId.split('-')[0];
                  // @ts-expect-error
                  data = {...data, entityName, leadId, dontAddRelatedLeads: 1};
              } else if (entityName === 'Contacts' || entityName === 'Accounts') {
                  // @ts-expect-error
                  data.participants.push({entityName, entityId});
              }

              const params = new URLSearchParams();
              if (plaintextValue) {
                  params.set('note', plaintextValue);
              }
              if (activityTypeId) {
                  params.set('activityTypeId', activityTypeId);
              }
              if (durationMinutes) {
                  params.set('durationMinutes', durationMinutes.toString());
              }

              appHistory.replace({
                  ...appHistory.location,
                  hash: `/log/activities/${entityId}?${params.toString()}`,
                  state: {modal: true},
              });

              clearEditorInput();
          }
        : undefined;

    const footerBarStyles = classNames('flex', 'justify-sb', 'align-center', {
        ['border-top pad-16']: variant === 'timeline',
    });

    return (
        <div className={footerBarStyles} onKeyDown={handleKeyDown}>
            <div className={styles['left-container']}>
                {isActivity ? (
                    <>
                        <Stack isHorizontal={true} alignItems='center' spacing={8}>
                            <div className={styles.label}>Activity</div>
                            <div className={styles.picker}>
                                <GraphQLSelectActivityTypes
                                    // @ts-expect-error
                                    labelId='ActivityTypePicker'
                                    onChange={(selection) => setActivityTypeId(selection.id)}
                                    selectedId={activityTypeId}
                                    styles={localStyles}
                                    menuPlacement='auto'
                                    autoFocus={false}
                                    includeManageOption={true}
                                />
                            </div>
                        </Stack>
                        <Stack isHorizontal={true} alignItems='center' spacing={8}>
                            <div className={styles.label}>Duration</div>
                            <div className={styles.picker}>
                                <SelectPickerGeneric
                                    options={durationOptions}
                                    value={
                                        durationMinutes
                                            ? durationOptions.find((obj) => {
                                                  return obj.value === durationMinutes;
                                              })
                                            : defaultDurationOption
                                    }
                                    // @ts-expect-error
                                    onChange={(selection) => setDurationMinutes(selection.value)}
                                    styles={localStyles}
                                    menuPlacement='auto'
                                    controlIcon={
                                        variant === 'lead-page' ? SolidClockIcon : undefined
                                    }
                                />
                            </div>
                            <Button variant='text-primary' size='small' onClick={onClickMore}>
                                More options
                            </Button>
                        </Stack>
                    </>
                ) : null}
                {variant === 'lead-page' && isNote ? (
                    <Checkbox checked={pinNote} onChange={setPinNote} label='Pin note' />
                ) : null}
            </div>
            <Stack spacing={8} isHorizontal={true}>
                {variant === 'lead-page' && (
                    <Button variant='text-secondary' size='small' onClick={handleClose}>
                        Cancel
                    </Button>
                )}
                <SaveButton
                    type='submit'
                    variant={variant === 'lead-page' ? 'primary' : 'save'}
                    // @ts-expect-error
                    onClick={handleSubmit}
                    resetOnComplete={true}
                    isSaving={isLoading}
                    buttonText={contentType === 'ACTIVITY' ? 'Log activity' : 'Save note'}
                    savingText={contentType === 'ACTIVITY' ? 'Logging…' : 'Saving…'}
                    successText='Success!'
                    disabled={isLoading || isDisabled}
                    getButtonRef={getSubmitButtonRef}
                />
            </Stack>
        </div>
    );
}
